import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, inject, Injector, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { provideComponentStore } from '@ngrx/component-store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PasConfirmationComponent,
  PasNavbarComponent,
  PasNavElementComponent,
  PasNavUserareaComponent,
  PasNavUserElementDirective,
  PasNotificationsComponent,
  PasSidebarHandleService,
} from '@porsche-aftersales/frontend-toolbox';
import { NgxSpinnerComponent } from 'ngx-spinner';

import { environment } from '../../environments/environment';

import { setAppInjector } from './app-injector';
import { ContentComponent } from './content/content.component';
import { EnvironmentConfigService } from './core/environment-config/environment-config.service';
import { LOGIN_FALLBACK } from './core/global';
import { hasAfterSalesReadOrSuppliersAndRatesRight } from './services/after-sales/after-sales-functions';
import { ContextService } from './services/context/context.service';
import { BookingRoutingService } from './services/details-view-service/booking-routing.service';
import { FieldMasterDataRoutingService } from './services/details-view-service/supplier-parts-routing.service';
import { LoginService } from './services/user-service/login.service';
import { User } from './services/user-service/model/user.model';
import { UserRights } from './services/user-service/user-rights.enum';
import { UserService } from './services/user-service/user.service';
import { DynamicLocaleService } from './shared/dynamic-localization-loader/dynamic-locale.service';
import { GlobalStore } from './shared/global.store';
import { SpinnerComponent } from './shared/spinner/spinner.component';
import { StickyFooterComponent } from './shared/sticky-footer/sticky-footer.component';

declare const UI_VERSION: any;
declare const UI_BUILD_DATE: any;

@Component({
  selector: 'tur-my-app',
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
  providers: [provideComponentStore(GlobalStore)],
  imports: [
    TranslatePipe,
    PasNavbarComponent,
    PasNavElementComponent,
    PasNavUserareaComponent,
    PasNavUserElementDirective,
    PasNotificationsComponent,
    PasConfirmationComponent,
    ContentComponent,
    NgxSpinnerComponent,
    StickyFooterComponent,
    SpinnerComponent,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit {
  private readonly loginService = inject(LoginService);
  private readonly userService = inject(UserService);
  private readonly translate = inject(TranslateService);
  private readonly dynamicLocaleService = inject(DynamicLocaleService);
  private readonly router = inject(Router);
  private readonly contextService = inject(ContextService);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly bookingRoutingService = inject(BookingRoutingService);
  private readonly fieldMasterDataRoutingService = inject(FieldMasterDataRoutingService);
  private readonly globalStore = inject(GlobalStore);
  private readonly injector = inject(Injector);
  private readonly elementRef = inject(ElementRef);
  private readonly environmentConfigService = inject(EnvironmentConfigService);
  public user: User;

  public buildDate: string;
  public backendVersion = ` ${undefined}`;
  public uiBuildDate: string;
  public uiVersion = ` ${undefined}`;

  public readonly isSpinnerActive$ = this.globalStore.isProcessing$;

  public constructor() {
    const injector = this.injector;

    // TODO: Find a better way to set/inject Injector
    setAppInjector(injector);
  }

  public ngOnInit(): void {
    // for application start
    this.user = this.userService.getUser();
    this.translate.use(this.userService.userLanguage);
    this.dynamicLocaleService.setLocale();

    this.getVersions();
    this.getBuildDate();
    this.setUiVersionAndBuildDate();

    this.elementRef.nativeElement.removeAttribute('ng-version');
  }

  private getFallbackUrl(baseUrl: HTMLAnchorElement): string {
    const fallbackUrls: Record<string, string> =
      environment.deploymentTarget === 'onprem'
        ? {
            'ppnlite.porsche.com': environment.LOGIN_FALLBACK_PPN_LITE,
            'ppn.porsche.com': environment.LOGIN_FALLBACK_PPN,
          }
        : {
            'ppnlite.porsche.com': this.environmentConfigService.snapshot()?.LOGIN_FALLBACK_PPN_LITE ?? '',
            'ppn.porsche.com': this.environmentConfigService.snapshot()?.LOGIN_FALLBACK_PPN ?? '',
          };
    return fallbackUrls[baseUrl.hostname] || LOGIN_FALLBACK;
  }

  public onNavigateRecourseBooking(): void {
    this.bookingRoutingService.openBookingsOverview();
    this.sidebarHandleService.enableSidebarForView();
  }

  public onNavigateRecourseCampaign(): void {
    this.sidebarHandleService.enableSidebarForView();
  }

  public onNavigateApproval(): void {
    this.sidebarHandleService.enableSidebarForView();
  }

  public onNavigateAfterSales(): void {
    this.router.navigate([`/afterSales/tabs/overview`]);
    this.sidebarHandleService.enableSidebarForView();
  }

  /* Temporary turned off
  public onNavigateClaimRecourse(): void {
    this.sidebarHandleService.enableSidebarForView();
  }

  public claimRecourseTabVisible(): boolean {
    return (
      this.user?.rights.includes(UserRights.CAMPAIGNS_BOOKING) || this.user?.rights.includes(UserRights.RECOURSE_READ)
    );
  }
  */

  public onLogout(): void {
    this.userService.logoutUser();
    const baseUrl = this.contextService.getBaseUrl('/');
    const fallbackUrl = this.getFallbackUrl(baseUrl);
    // eslint-disable-next-line no-restricted-globals
    document.location.href = `${baseUrl}${fallbackUrl}`;
  }

  public recourseCampaignTabVisible(): boolean {
    return (
      this.user?.rights.includes(UserRights.CAMPAIGNS_READ) || this.user?.rights.includes(UserRights.CAMPAIGNS_WORKFLOW)
    );
  }

  public approvalTabVisible(): boolean {
    return (
      this.user?.rights.includes(UserRights.CAMPAIGNS_APPROVE) ||
      this.user?.rights.includes(UserRights.RECOURSE_APPROVE) ||
      this.user?.rights.includes(UserRights.AFTERSALES_APPROVE) ||
      this.user?.rights.includes(UserRights.RAD_APPROVE)
    );
  }

  public afterSalesTabVisible(): boolean {
    return hasAfterSalesReadOrSuppliersAndRatesRight(this.user?.rights);
  }

  public fieldMasterDataTabVisible(): boolean {
    return this.user?.rights.includes(UserRights.RECOURSE_READ);
  }

  public onNavigateSupplierParts(): void {
    this.fieldMasterDataRoutingService.routeToTabPartsAgreement();
    this.sidebarHandleService.enableSidebarForView();
  }

  private getVersions(): void {
    if (this.loginService.getVersion() != null) {
      this.backendVersion = this.loginService.getVersion(); //this.translate.instant("BUILD_VERSION") + ": " +
    }
  }

  private getBuildDate(): void {
    if (this.loginService.getBuildDate() != null) {
      this.buildDate = this.loginService.getBuildDate(); //this.translate.instant("BUILD_DATE") + ": " +
    }
  }

  private setUiVersionAndBuildDate(): void {
    this.uiVersion = UI_VERSION;
    this.uiBuildDate = UI_BUILD_DATE;
  }
}
